import { useQuery } from "@tanstack/react-query";
import { Command as CommandPrimitive } from "cmdk";
import {
  Clock,
  FileText,
  Folder,
  Globe,
  ImageIcon,
  Search,
  ChevronRight,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import {
  getFolderNameById,
  getItemTagNames,
  getSourceName,
  mineApi,
  useOpenItemPreview,
  useSidebarData,
} from "@/modules/mine";
import type { FolderData, Item, TagData } from "@/modules/mine/types";
import { dialogManager } from "@/shared/components/dialog";
import {
  ROUTE_PATHS,
  buildMineFolderPath,
  buildMineTagPath,
} from "@/shared/constants/route-paths";
import { STORAGE_KEYS } from "@/shared/constants/storage-keys";
import { storage } from "@/shared/lib/storage";
import { cn } from "@/shared/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/shared/ui/command";
import {
  InputGroup,
  InputGroupAddon,
} from "@/shared/ui/input-group";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/shared/ui/popover";

const MAX_RECENT_SEARCHES = 8;
const SEARCH_PREVIEW_LIMIT = 5;
const SEARCH_DEBOUNCE_MS = 300;

interface FolderSearchOption {
  id: string;
  name: string;
  parentNames: string[];
}

function readRecentSearches() {
  const searches = storage.getItem<string[]>(STORAGE_KEYS.recentSearches, []);

  return Array.isArray(searches)
    ? searches.filter((item): item is string => typeof item === "string")
    : [];
}

function writeRecentSearches(searches: string[]) {
  storage.setItem(
    STORAGE_KEYS.recentSearches,
    searches.slice(0, MAX_RECENT_SEARCHES),
  );
}

function flattenFolders(
  folders: FolderData[],
  parentNames: string[] = [],
): FolderSearchOption[] {
  return folders.flatMap<FolderSearchOption>((folder) => {
    const currentNames = [...parentNames, folder.name];
    const children = folder.sub_folders?.length
      ? flattenFolders(folder.sub_folders, currentNames)
      : [];

    return [
      {
        id: folder.id,
        name: folder.name,
        parentNames,
      },
      ...children,
    ];
  });
}

function normalizeKeyword(value: string) {
  return value.trim();
}

function useDebouncedValue(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [delay, value]);

  return debouncedValue;
}

function getSafeHostname(url?: string) {
  if (!url) {
    return "";
  }

  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return getSourceName(url);
  }
}

function getSearchItemTitle(item: Item) {
  const title = item.title?.trim();

  if (title) {
    return title;
  }

  if (item.format === "website" && item.url) {
    return getSafeHostname(item.url);
  }

  if (item.format === "image") {
    return item.url ? getSourceName(item.url) : "未命名图片";
  }

  if (item.format === "text") {
    return "未命名文本";
  }

  return "未命名收藏";
}

function getSearchItemIcon(item: Item) {
  if (item.format === "website") {
    return <Globe />;
  }

  if (item.format === "image") {
    return <ImageIcon />;
  }

  return <FileText />;
}

function getSearchItemIconBoxClass(item: Item) {
  if (item.format === "website") {
    return "bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300";
  }

  if (item.format === "image") {
    return "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-300";
  }

  return "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300";
}

function getSearchItemIconClass(item: Item) {
  if (item.format === "website") {
    return "text-blue-600 dark:text-blue-300";
  }

  if (item.format === "text") {
    return "text-emerald-600 dark:text-emerald-300";
  }

  return "text-zinc-500 dark:text-zinc-300";
}

function getSearchItemTypeLabel(item: Item) {
  if (item.format === "website") {
    return "网页";
  }

  if (item.format === "image") {
    return "图片";
  }

  if (item.format === "text") {
    return "笔记";
  }

  return "收藏";
}

function getSearchItemMeta(item: Item, folders: FolderData[]) {
  const folderName = getFolderNameById(item.folder_id ?? "", folders);

  if (item.format === "website") {
    return {
      folderName,
      description: getSafeHostname(item.url),
    };
  }

  const tagNames = getItemTagNames(item.tags).slice(0, 2);

  if (tagNames.length > 0) {
    return {
      folderName,
      description: tagNames.map((tag) => `#${tag}`).join("，"),
    };
  }

  return {
    folderName,
    description: item.note?.trim() || "",
  };
}

function SearchComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { folders, tags } = useSidebarData();
  const openPreviewItem = useOpenItemPreview();
  const [query, setQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const hasOpenDialog = useSyncExternalStore(dialogManager.subscribe, dialogManager.getState, dialogManager.getState).length > 0;
  const [isComposing, setIsComposing] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const routeSearchQuery = searchParams.get("q") ?? "";
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isComposingRef = useRef(false);

  useEffect(() => {
    setRecentSearches(readRecentSearches());
  }, []);

  useEffect(() => {
    if (hasOpenDialog) setOpen(false);
  }, [hasOpenDialog]);

  useEffect(() => {
    if (location.pathname === ROUTE_PATHS.MINE_SEARCH) {
      setQuery(routeSearchQuery);
      setSearchQuery(routeSearchQuery);
      return;
    }

    setQuery("");
    setSearchQuery("");
  }, [location.pathname, routeSearchQuery]);

  const flattenedFolders = useMemo(() => flattenFolders(folders), [folders]);
  const keyword = isComposing ? "" : normalizeKeyword(searchQuery);
  const debouncedKeyword = useDebouncedValue(keyword, SEARCH_DEBOUNCE_MS);

  const searchPreviewQuery = useQuery({
    queryKey: ["mine", "search-preview", debouncedKeyword],
    queryFn: () =>
      mineApi.getItems(
        {
          page: 1,
          limit: SEARCH_PREVIEW_LIMIT,
          q: debouncedKeyword,
          folder_id: "-1",
          order: "id",
          sort: "desc",
        },
        { silent: true },
      ),
    enabled: open && debouncedKeyword.length > 0,
  });

  const previewItems = useMemo(
    () => searchPreviewQuery.data?.items ?? [],
    [searchPreviewQuery.data?.items],
  );
  const isSearchPreviewLoading =
    keyword.length > 0 &&
    debouncedKeyword.length > 0 &&
    searchPreviewQuery.isFetching;

  const matchedFolders = useMemo(() => {
    if (!keyword) {
      return [];
    }

    return flattenedFolders
      .filter((folder) => folder.name.toLowerCase().includes(keyword.toLowerCase()));
  }, [flattenedFolders, keyword]);

  const matchedTags = useMemo(() => {
    if (!keyword) {
      return [];
    }

    return tags
      .filter((tag) => tag.name.toLowerCase().includes(keyword.toLowerCase()));
  }, [keyword, tags]);

  const saveRecentSearch = useCallback((value: string) => {
    const nextSearches = [
      value,
      ...recentSearches.filter((item) => item !== value),
    ].slice(0, MAX_RECENT_SEARCHES);

    setRecentSearches(nextSearches);
    writeRecentSearches(nextSearches);
  }, [recentSearches]);

  const removeRecentSearch = useCallback((value: string) => {
    const nextSearches = recentSearches.filter((item) => item !== value);

    setRecentSearches(nextSearches);
    writeRecentSearches(nextSearches);
  }, [recentSearches]);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    writeRecentSearches([]);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const searchItems = useCallback((value = query) => {
    const nextKeyword = normalizeKeyword(value);

    if (!nextKeyword) {
      setOpen(true);
      return;
    }

    saveRecentSearch(nextKeyword);
    setOpen(false);
    navigate(`${ROUTE_PATHS.MINE_SEARCH}?q=${encodeURIComponent(nextKeyword)}`);
  }, [navigate, query, saveRecentSearch]);

  const navigateToFolder = useCallback((folder: FolderSearchOption) => {
    setOpen(false);
    navigate(buildMineFolderPath(folder.id));
  }, [navigate]);

  const navigateToTag = useCallback((tag: TagData) => {
    setOpen(false);
    navigate(buildMineTagPath(tag.id));
  }, [navigate]);

  const openItemPreview = useCallback((item: Item) => {
    setOpen(false);
    openPreviewItem(item, previewItems);
  }, [openPreviewItem, previewItems]);

  const shouldShowRecent = !keyword && recentSearches.length > 0;
  const shouldShowMatches = !!keyword;
  const hasMatches = matchedFolders.length > 0 || matchedTags.length > 0;

  return (
    <Popover open={open && !hasOpenDialog} onOpenChange={setOpen}>
      <Command
        shouldFilter={false}
        className="h-9 overflow-visible rounded-none bg-transparent text-foreground"
      >
        <PopoverAnchor asChild>
          <div ref={searchBoxRef} className="w-full max-w-xl">
            <InputGroup data-workspace-search className="rounded-lg bg-card">
              <CommandPrimitive.Input
                ref={inputRef}
                name="workspace-search"
                autoComplete="off"
                role="searchbox"
                inputMode="search"
                data-slot="input-group-control"
                placeholder="搜索收藏、文件夹、标签..."
                value={query}
                onValueChange={(value) => {
                  if (hasOpenDialog || document.activeElement !== inputRef.current) {
                    return;
                  }
                  setQuery(value);
                  if (!isComposingRef.current) {
                    setSearchQuery(value);
                  }
                  setOpen(true);
                }}
                onCompositionStart={() => {
                  isComposingRef.current = true;
                  setIsComposing(true);
                }}
                onCompositionEnd={(event) => {
                  isComposingRef.current = false;
                  setIsComposing(false);
                  if (hasOpenDialog) return;
                  setQuery(event.currentTarget.value);
                  setSearchQuery(event.currentTarget.value);
                  setOpen(true);
                }}
                onFocus={() => {
                  if (!hasOpenDialog) setOpen(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setOpen(false);
                  }
                }}
                className={cn(
                  "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground h-9 w-full min-w-0 px-3 py-1 text-base outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                  "flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent",
                )}
              />
              <InputGroupAddon>
                <Search />
              </InputGroupAddon>
              <InputGroupAddon align="inline-end" className="gap-0 pr-3">
                {query && (
                  <button
                    type="button"
                    title="清除搜索"
                    className="inline-flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    onClick={() => {
                      setQuery("");
                      setSearchQuery("");
                      setOpen(true);
                      window.requestAnimationFrame(() => {
                        inputRef.current?.focus();
                      });
                    }}
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </InputGroupAddon>
            </InputGroup>
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-[var(--radix-popper-anchor-width)] overflow-hidden p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            const target = event.target;

            if (
              target instanceof Node &&
              searchBoxRef.current?.contains(target)
            ) {
              event.preventDefault();
            }
          }}
        >
          <CommandList className="max-h-[440px]">
            {shouldShowRecent && (
              <CommandGroup
                heading={
                  <div className="flex items-center justify-between gap-2">
                    <span>最近搜索</span>
                    <button
                      type="button"
                      className="cursor-pointer rounded-sm px-1.5 py-0.5 text-xs font-normal text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        clearRecentSearches();
                      }}
                    >
                      清除
                    </button>
                  </div>
                }
              >
                {recentSearches.map((item) => (
                  <CommandItem
                    key={item}
                    value={`recent-${item}`}
                    onSelect={() => searchItems(item)}
                    className="group cursor-pointer"
                  >
                    <Clock />
                    <span className="truncate">{item}</span>
                    <button
                      type="button"
                      title="删除记录"
                      className="ml-auto hidden size-5 cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-background hover:text-foreground group-hover:inline-flex"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        removeRecentSearch(item);
                      }}
                    >
                      <X className="size-3.5" />
                    </button>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {shouldShowMatches && (
              <>
                <CommandGroup>
                  <CommandItem
                    value={`search-${keyword}`}
                    onSelect={() => searchItems()}
                    className="cursor-pointer"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">
                      查看全部搜索结果 “{keyword}”
                    </span>
                    <ChevronRight className="ml-auto size-4 text-muted-foreground" />
                  </CommandItem>
                </CommandGroup>

                <CommandSeparator alwaysRender className="mx-2 bg-border/60" />

                <CommandGroup heading={`收藏`}>
                  {previewItems.map((item) => {
                    const meta = getSearchItemMeta(item, folders);

                    return (
                      <CommandItem
                        key={item.id}
                        value={`item-${item.id}`}
                        onSelect={() => openItemPreview(item)}
                        className="cursor-pointer items-center py-2"
                      >
                        <span
                          className={cn(
                            "flex size-7 shrink-0 items-center justify-center rounded-md",
                            getSearchItemIconBoxClass(item),
                            "[&_svg]:size-4",
                            getSearchItemIconClass(item),
                          )}
                        >
                          {getSearchItemIcon(item)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">
                            {getSearchItemTitle(item)}
                          </div>
                          <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground/70">
                            {meta.folderName && (
                              <span className="min-w-0 truncate">
                                <span className="truncate">{meta.folderName}</span>
                              </span>
                            )}
                            {meta.folderName && meta.description && (
                              <span className="shrink-0 text-muted-foreground/40">·</span>
                            )}
                            {meta.description && (
                              <span className="truncate">{meta.description}</span>
                            )}
                          </div>
                        </div>
                        <CommandShortcut className="rounded bg-muted/70 px-1.5 py-0.5 tracking-normal text-muted-foreground/80">
                          {getSearchItemTypeLabel(item)}
                        </CommandShortcut>
                      </CommandItem>
                    );
                  })}

                  {!isSearchPreviewLoading && previewItems.length === 0 && (
                    <div className="px-2 py-2 text-sm text-muted-foreground">
                      没有匹配的收藏
                    </div>
                  )}

                  {isSearchPreviewLoading && (
                    <div className="px-2 py-2 text-sm text-muted-foreground">
                      正在搜索收藏...
                    </div>
                  )}
                </CommandGroup>

                {(matchedFolders.length > 0 || matchedTags.length > 0) && (
                  <CommandSeparator alwaysRender className="mx-2 bg-border/60" />
                )}

                {matchedFolders.length > 0 && (
                  <CommandGroup heading="文件夹">
                    {matchedFolders.map((folder) => (
                      <CommandItem
                        key={folder.id}
                        value={`folder-${folder.id}`}
                        onSelect={() => navigateToFolder(folder)}
                        className="cursor-pointer"
                      >
                        <Folder />
                        <div className="flex min-w-0 flex-1 items-center gap-1 truncate">
                          {folder.parentNames.map((parentName, index) => (
                            <span
                              key={`${folder.id}-${index}-${parentName}`}
                              className="min-w-0 truncate text-muted-foreground"
                            >
                              {parentName}
                              <span className="ml-1 text-muted-foreground/60">/</span>
                            </span>
                          ))}
                          <span className="min-w-0 truncate font-medium">
                            {folder.name}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {matchedTags.length > 0 && (
                  <CommandGroup heading="标签">
                    {matchedTags.map((tag) => (
                      <CommandItem
                        key={tag.id}
                        value={`tag-${tag.id}`}
                        onSelect={() => navigateToTag(tag)}
                        className="cursor-pointer"
                      >
                        <span className="truncate">#{tag.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {!hasMatches && (
                  <CommandEmpty>没有匹配的文件夹或标签</CommandEmpty>
                )}
              </>
            )}

            {!shouldShowRecent && !shouldShowMatches && (
              <CommandEmpty>
                <div className="flex flex-col items-center justify-center gap-2 py-2 text-muted-foreground">
                  <span className="flex size-8 items-center justify-center rounded-full bg-muted/60">
                    <Search className="size-4 opacity-60" />
                  </span>
                  <span className="text-sm text-muted-foreground/70">
                    输入关键词搜索收藏、文件夹和标签
                  </span>
                </div>
              </CommandEmpty>
            )}
          </CommandList>
        </PopoverContent>
      </Command>
    </Popover>
  );
}

export default SearchComponent;
